// components/public/Footer.jsx
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Twitter, Youtube, Linkedin, Globe, MapPin, Phone, Mail } from 'lucide-react';
import { getRedesSocialesPublicas, getRestauranteActual } from '../../api/public';

const Footer = () => {
  const [redesSociales, setRedesSociales] = useState([]);
  const [restaurante, setRestaurante] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        // Cargar redes sociales
        const redes = await getRedesSocialesPublicas();
        setRedesSociales(redes);

        // Cargar información del restaurante
        const restauranteData = await getRestauranteActual();
        setRestaurante(restauranteData);
      } catch (error) {
        console.error('Error cargando datos del footer:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Función para obtener el icono según el tipo
  const getIcon = (tipo) => {
    const tipoLower = tipo.toLowerCase();
    if (tipoLower.includes('facebook')) return <Facebook className="w-5 h-5" />;
    if (tipoLower.includes('instagram')) return <Instagram className="w-5 h-5" />;
    if (tipoLower.includes('twitter') || tipoLower.includes('x')) return <Twitter className="w-5 h-5" />;
    if (tipoLower.includes('youtube')) return <Youtube className="w-5 h-5" />;
    if (tipoLower.includes('linkedin')) return <Linkedin className="w-5 h-5" />;
    return <Globe className="w-5 h-5" />;
  };

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-12">
        {/* Contenedor de 2 columnas en móvil y 4 columnas en desktop */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-6 sm:gap-8">
          {/* Columna 1: Sobre Nosotros */}
          <div>
            <h3 className="text-sm sm:text-xl font-bold mb-2 sm:mb-4 text-red-500 tracking-wide">Sobre Nosotros</h3>
            {loading ? (
              <div className="animate-pulse space-y-1.5">
                <div className="h-3 bg-gray-700 rounded w-full"></div>
                <div className="h-3 bg-gray-700 rounded w-4/5"></div>
                <div className="h-3 bg-gray-700 rounded w-3/5"></div>
              </div>
            ) : (
              <p className="text-gray-400 text-xs sm:text-sm leading-relaxed line-clamp-5 sm:line-clamp-none">
                {restaurante?.historia || restaurante?.eslogan || 
                  'Cadena de restaurantes comprometida con ofrecer la mejor experiencia gastronómica tradicional con productos frescos y sabor auténtico.'}
              </p>
            )}
          </div>

          {/* Columna 2: Enlaces Rápidos */}
          <div>
            <h3 className="text-sm sm:text-xl font-bold mb-2 sm:mb-4 text-red-500 tracking-wide">Enlaces Rápidos</h3>
            <ul className="space-y-1.5 sm:space-y-2">
              <li>
                <Link to="/menu" className="text-gray-400 hover:text-white transition-colors text-xs sm:text-sm block py-0.5">
                  Nuestro Menú
                </Link>
              </li>
              <li>
                <Link to="/promociones" className="text-gray-400 hover:text-white transition-colors text-xs sm:text-sm block py-0.5">
                  Promociones
                </Link>
              </li>
              <li>
                <Link to="/restaurantes" className="text-gray-400 hover:text-white transition-colors text-xs sm:text-sm block py-0.5">
                  Ubicaciones
                </Link>
              </li>
              <li>
                <Link to="/contacto" className="text-gray-400 hover:text-white transition-colors text-xs sm:text-sm block py-0.5">
                  Contáctanos
                </Link>
              </li>
            </ul>
          </div>

          {/* Columna 3: Contacto */}
          <div>
            <h3 className="text-sm sm:text-xl font-bold mb-2 sm:mb-4 text-red-500 tracking-wide">Contacto</h3>
            {loading ? (
              <div className="space-y-2">
                <div className="animate-pulse flex items-center gap-2">
                  <div className="w-4 h-4 bg-gray-700 rounded"></div>
                  <div className="h-3 bg-gray-700 rounded w-24"></div>
                </div>
                <div className="animate-pulse flex items-center gap-2">
                  <div className="w-4 h-4 bg-gray-700 rounded"></div>
                  <div className="h-3 bg-gray-700 rounded w-20"></div>
                </div>
              </div>
            ) : (
              <ul className="space-y-2 sm:space-y-3">
                {restaurante?.direccion && (
                  <li className="flex items-start gap-1.5 sm:gap-2">
                    <MapPin className="w-3.5 h-3.5 sm:w-5 sm:h-5 text-red-500 shrink-0 mt-0.5" />
                    <span className="text-gray-400 text-[11px] sm:text-sm leading-snug break-words">
                      {restaurante.direccion}
                    </span>
                  </li>
                )}
                {restaurante?.telefono && (
                  <li className="flex items-center gap-1.5 sm:gap-2">
                    <Phone className="w-3.5 h-3.5 sm:w-5 sm:h-5 text-red-500 shrink-0" />
                    <a 
                      href={`tel:${restaurante.telefono}`}
                      className="text-gray-400 text-[11px] sm:text-sm hover:text-white transition-colors truncate"
                    >
                      {restaurante.telefono}
                    </a>
                  </li>
                )}
                {restaurante?.correo && (
                  <li className="flex items-center gap-1.5 sm:gap-2">
                    <Mail className="w-3.5 h-3.5 sm:w-5 sm:h-5 text-red-500 shrink-0" />
                    <a 
                      href={`mailto:${restaurante.correo}`}
                      className="text-gray-400 text-[11px] sm:text-sm hover:text-white transition-colors truncate"
                    >
                      {restaurante.correo}
                    </a>
                  </li>
                )}
                {!restaurante?.direccion && !restaurante?.telefono && !restaurante?.correo && (
                  <li className="text-gray-400 text-[11px] sm:text-sm">
                    Información no disponible
                  </li>
                )}
              </ul>
            )}
          </div>

          {/* Columna 4: Síguenos */}
          <div>
            <h3 className="text-sm sm:text-xl font-bold mb-2 sm:mb-4 text-red-500 tracking-wide">Síguenos</h3>
            {loading ? (
              <div className="flex gap-2">
                {[1, 2].map((i) => (
                  <div key={i} className="w-8 h-8 bg-gray-700 rounded-full animate-pulse"></div>
                ))}
              </div>
            ) : redesSociales.length > 0 ? (
              <>
                <div className="flex flex-wrap gap-2 sm:gap-3">
                  {redesSociales.map((red) => (
                    <a
                      key={red.id_red}
                      href={red.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-gray-800 p-2 sm:p-3 rounded-full hover:bg-red-600 transition-colors"
                      title={red.tipo}
                    >
                      {getIcon(red.tipo)}
                    </a>
                  ))}
                </div>
                <p className="text-gray-400 text-[11px] sm:text-sm mt-2 sm:mt-4 leading-tight">
                  Mantente al día con nuestras novedades
                </p>
              </>
            ) : (
              <p className="text-gray-400 text-[11px] sm:text-sm">
                Próximamente en redes sociales
              </p>
            )}
          </div>
        </div>

        {/* Copyright con enlace a PoliDevs */}
        <div className="border-t border-gray-800/80 mt-6 pt-4 sm:mt-8 sm:pt-8 text-center">
          <p className="text-gray-400 text-[11px] sm:text-sm leading-relaxed">
            Desarrollado por{' '}
            <a
              href="https://www.polidevs.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-red-500 hover:text-red-400 font-semibold transition-colors"
            >
              PoliDevs
            </a>
            {' '}© {new Date().getFullYear()} {restaurante?.nombre_comercial || 'Cadena de Restaurantes'}. Todos los derechos reservados.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
